/**
 * facets.js
 * AJAX collection filtering (facets) via Shopify Section Rendering API.
 *
 * Features:
 * - Checkbox / color swatch filters: instant AJAX filtering.
 * - Price range slider: live visual update while dragging, auto-filters
 *   the moment the handle is released (native "change" event).
 * - Price number inputs: manual, applied via Apply button or Enter key.
 * - Dropdown open/collapsed state is preserved across AJAX sidebar swaps.
 * - Show more / show less toggle for filter lists with many options.
 */

class FacetFilters {
  constructor() {
    this.productContainer = document.getElementById('js-product-ajax');
    if (!this.productContainer) return;

    this.sectionId = this.productContainer.dataset.sectionId;
    this.collectionUrl = window.location.pathname;

    this.bindEvents();
    this.initPriceSliders();
  }

  bindEvents() {
    // Checkbox filters (list & boolean type) — instant
    document.addEventListener('change', (e) => {
      if (e.target.matches('.js-filter-checkbox')) {
        const url = e.target.checked
          ? e.target.dataset.addUrl
          : e.target.dataset.removeUrl;
        if (url) this.renderFromUrl(url);
      }
    });

    // Color swatch links + active filter chips
    document.addEventListener('click', (e) => {
      const link = e.target.closest('.js-filter-link');
      if (link) {
        e.preventDefault();
        this.renderFromUrl(link.getAttribute('href'));
      }
    });

    // Clear all filters
    document.addEventListener('click', (e) => {
      if (e.target.closest('.js-clear-filter')) {
        e.preventDefault();
        this.renderFromUrl(this.collectionUrl);
      }
    });

    // Price — Apply button (manual trigger, used for number-input edits)
    document.addEventListener('click', (e) => {
      const applyBtn = e.target.closest('.js-price-apply');
      if (applyBtn) {
        e.preventDefault();
        const wrap = applyBtn.closest('.filter-price-range');
        this.submitPriceForm(wrap);
        wrap.classList.remove('is-dirty');
      }
    });

    // Price — Enter key inside number inputs also applies
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && e.target.matches('.js-price-min, .js-price-max')) {
        e.preventDefault();
        const wrap = e.target.closest('.filter-price-range');
        this.submitPriceForm(wrap);
        wrap.classList.remove('is-dirty');
      }
    });

    // Sort by dropdown
    document.addEventListener('change', (e) => {
      if (e.target.matches('[name="sort_by"]')) {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', e.target.value);
        this.renderFromUrl(url.pathname + url.search);
      }
    });

    // Pagination links inside product grid
    document.addEventListener('click', (e) => {
      const pageLink = e.target.closest('.js-pagination a');
      if (pageLink) {
        e.preventDefault();
        this.renderFromUrl(pageLink.getAttribute('href'));
      }
    });

    // Collapse/expand tiap filter block
    document.addEventListener('click', (e) => {
      const header = e.target.closest('.js-filter-toggle');
      if (header) {
        const content = header.nextElementSibling;
        const icon = header.querySelector('.js-filter-icon');
        content.classList.toggle('collapsed');
        icon?.classList.toggle('rotated');
      }
    });

    // Show more / show less toggle within a filter list
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.js-filter-show-more');
      if (btn) {
        const list = btn.previousElementSibling; // the <ul>
        if (!list) return;
        const hiddenItems = list.querySelectorAll('.filter-item-extra');
        const label = btn.querySelector('.js-show-more-label');
        const isExpanded = btn.dataset.expanded === 'true';

        hiddenItems.forEach((item) => {
          item.classList.toggle('is-hidden', isExpanded);
        });

        if (isExpanded) {
          label.textContent = `Show more (${hiddenItems.length})`;
          btn.dataset.expanded = 'false';
        } else {
          label.textContent = 'Show less';
          btn.dataset.expanded = 'true';
        }
      }
    });

    // Mobile sidebar toggle
    document.addEventListener('click', (e) => {
      const toggle = e.target.closest('.js-toggle-filter');
      console.log('toggle')
      if (toggle) {
        toggle.classList.toggle('active');
        let sidebarFilter = document.querySelector('.js-sidebar');
        if(sidebarFilter.classList.contains('active')){
            sidebarFilter.classList.remove('active');
        }else{
            sidebarFilter.classList.add('active');
        }
      }
    });

    // Per-row view switcher
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.js-btn-view');
      if (btn) {
        const col = btn.dataset.col;
        document.querySelectorAll('.js-btn-view').forEach((b) => b.classList.remove('active'));
        btn.classList.add('active');
        document.querySelectorAll('.js-col').forEach((el) => {
          el.className = el.className.replace(/col-lg-\S+/g, '');
          if (col === '3') el.classList.add('col-lg-4');
          if (col === '4') el.classList.add('col-lg-3');
        });
      }
    });

    // Re-init price sliders after every AJAX swap (new DOM nodes need binding)
    document.addEventListener('facets:updated', () => {
      this.initPriceSliders();
    });
  }

  /* =========================================================
     PRICE SLIDER
     - "input" event: live visual update only (no fetch)
     - "change" event (fires once, on release): auto-triggers filter
     - number inputs: manual via Apply button / Enter key
     ========================================================= */
  initPriceSliders() {
    document.querySelectorAll('.filter-price-range').forEach((wrap) => {
      if (wrap.dataset.sliderInit) return; // avoid double-binding
      wrap.dataset.sliderInit = 'true';

      const min = parseFloat(wrap.dataset.rangeMin) || 0;
      const max = parseFloat(wrap.dataset.rangeMax) || 100;

      const rangeMin = wrap.querySelector('.js-price-range-min');
      const rangeMax = wrap.querySelector('.js-price-range-max');
      const numMin = wrap.querySelector('.js-price-min');
      const numMax = wrap.querySelector('.js-price-max');
      const displayMin = wrap.querySelector('.js-price-display-min');
      const displayMax = wrap.querySelector('.js-price-display-max');
      const fill = wrap.querySelector('.js-price-track-fill');

      // SAFETY: force-sync native slider min/max attributes to the wrapper's
      // data attributes, in case HTML rendering ever mismatched them.
      rangeMin.min = min; rangeMin.max = max;
      rangeMax.min = min; rangeMax.max = max;
      if (parseFloat(rangeMin.value) > max) rangeMin.value = max;
      if (parseFloat(rangeMax.value) > max) rangeMax.value = max;

      const money = (val) => {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' })
          .format(val);
      };

      const updateFill = () => {
        const vMin = parseFloat(rangeMin.value);
        const vMax = parseFloat(rangeMax.value);
        const pctMin = ((vMin - min) / (max - min)) * 100;
        const pctMax = ((vMax - min) / (max - min)) * 100;
        fill.style.left = `${pctMin}%`;
        fill.style.right = `${100 - pctMax}%`;
      };

      const markDirty = () => wrap.classList.add('is-dirty');
      const clearDirty = () => wrap.classList.remove('is-dirty');

      // ===== Live visual update while dragging (no fetch yet) =====
      rangeMin.addEventListener('input', () => {
        let vMin = Math.min(parseFloat(rangeMin.value), max);
        const vMax = parseFloat(rangeMax.value);
        if (vMin > vMax) {
          vMin = vMax;
          rangeMin.value = vMin;
        }
        numMin.value = vMin;
        displayMin.textContent = money(vMin);
        updateFill();
        markDirty();
      });

      rangeMax.addEventListener('input', () => {
        let vMax = Math.min(parseFloat(rangeMax.value), max);
        const vMin = parseFloat(rangeMin.value);
        if (vMax < vMin) {
          vMax = vMin;
          rangeMax.value = vMax;
        }
        numMax.value = vMax;
        displayMax.textContent = money(vMax);
        updateFill();
        markDirty();
      });

      // ===== Auto-filter the moment the handle is released =====
      rangeMin.addEventListener('change', () => {
        this.submitPriceForm(wrap);
        clearDirty();
      });

      rangeMax.addEventListener('change', () => {
        this.submitPriceForm(wrap);
        clearDirty();
      });

      // ===== Number inputs stay manual: Apply button / Enter =====
      numMin.addEventListener('input', () => {
        let vMin = parseFloat(numMin.value) || min;
        vMin = Math.max(min, Math.min(vMin, parseFloat(rangeMax.value)));
        rangeMin.value = vMin;
        displayMin.textContent = money(vMin);
        updateFill();
        markDirty();
      });

      numMax.addEventListener('input', () => {
        let vMax = parseFloat(numMax.value) || max;
        vMax = Math.min(max, Math.max(vMax, parseFloat(rangeMin.value)));
        rangeMax.value = vMax;
        displayMax.textContent = money(vMax);
        updateFill();
        markDirty();
      });

      updateFill();
    });
  }

  submitPriceForm(wrap) {
    if (!wrap) return;
    const numMin = wrap.querySelector('.js-price-min');
    const numMax = wrap.querySelector('.js-price-max');

    const params = new URLSearchParams(window.location.search);
    if (numMin.value !== '') {
      params.set(numMin.name, numMin.value);
    } else {
      params.delete(numMin.name);
    }
    if (numMax.value !== '') {
      params.set(numMax.name, numMax.value);
    } else {
      params.delete(numMax.name);
    }

    const query = params.toString();
    this.renderFromUrl(`${this.collectionUrl}${query ? '?' + query : ''}`);
  }

  /* =========================================================
     DROPDOWN STATE PRESERVATION across AJAX swaps
     ========================================================= */
  captureOpenFilterState() {
    const openLabels = [];
    document.querySelectorAll('.filter-block').forEach((block) => {
      const content = block.querySelector('.filter-block__content');
      const title = block.querySelector('.filter-block__title');
      if (content && title && !content.classList.contains('collapsed')) {
        openLabels.push(title.textContent.trim());
      }
    });
    return openLabels;
  }

  restoreOpenFilterState(openLabels) {
    if (!openLabels.length) return;
    document.querySelectorAll('.filter-block').forEach((block) => {
      const content = block.querySelector('.filter-block__content');
      const title = block.querySelector('.filter-block__title');
      const icon = block.querySelector('.js-filter-icon');
      if (title && openLabels.includes(title.textContent.trim())) {
        content?.classList.remove('collapsed');
        icon?.classList.add('rotated');
      }
    });
  }

  renderFromUrl(url) {
    if (!url || !this.sectionId) return;

    // Save which dropdowns are open right now, before we blow away the sidebar
    const openLabels = this.captureOpenFilterState();

    this.setLoading(true);
    const separator = url.includes('?') ? '&' : '?';
    const fetchUrl = `${url}${separator}section_id=${this.sectionId}`;

    fetch(fetchUrl)
      .then((res) => {
        if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
        return res.text();
      })
      .then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        const newProductArea = doc.getElementById('js-product-ajax');
        if (newProductArea && this.productContainer) {
          this.productContainer.innerHTML = newProductArea.innerHTML;
        }

        const newSidebar = doc.querySelector('.collection-sidebar');
        const currentSidebar = document.querySelector('.collection-sidebar');
        if (newSidebar && currentSidebar) {
          currentSidebar.innerHTML = newSidebar.innerHTML;
        }

        const newTitle = doc.querySelector('.js-collection-title');
        const currentTitle = document.querySelector('.js-collection-title');
        if (newTitle && currentTitle) currentTitle.innerHTML = newTitle.innerHTML;

        window.history.pushState({}, '', url);

        // Put the dropdowns back the way the user had them
        this.restoreOpenFilterState(openLabels);

        this.setLoading(false);
        this.scrollToTop();

        document.dispatchEvent(new CustomEvent('facets:updated'));
      })
      .catch((err) => {
        console.error('[Facets] Failed to load filtered results:', err);
        this.setLoading(false);
        window.location.href = url;
      });
  }

  setLoading(isLoading) {
    this.productContainer?.classList.toggle('is-loading', isLoading);
  }

  scrollToTop() {
    document.querySelector('.category-heading')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  if (window.__facetFiltersInitialized) return;
  window.__facetFiltersInitialized = true;
  new FacetFilters();
});
window.addEventListener('popstate', () => window.location.reload());